import React, { useContext, useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Badge } from "@mui/material";

import { useStore } from "./context/StoreContext";
import { AuthContext } from "./context/AuthContext";
import { logout } from "./context/AuthActions";
import StoreSelector from "./components/forms/StoreSelector";
import MyChart from "./pages/analytics/Charts";

/* ---------- access ----------
   Works with the old shape  { user: { role, isAdmin } }
   and the new owner shape   { users_role: "owner", isOwner: true }          */
function getAccess(user) {
  const u = user?.user ?? user ?? {};
  const role = String(u.role ?? u.users_role ?? "");
  const isAdmin = Boolean(
    u.isAdmin || u.isOwner || role === "owner" || role === "admin",
  );
  return { u, role, isAdmin };
}

/* ---------- menu (same rules as before, one place) ---------- */
const NAV_ITEMS = [
  { to: "/", label: "Home", icon: "bi-house", show: (a) => a.isAdmin },
  {
    to: "/orders",
    label: "Orders",
    icon: "bi-border-style",
    show: (a) => a.isAdmin || a.role.includes("orders"),
  },
  {
    to: "/products",
    label: "Products",
    icon: "bi-grid",
    show: (a) => a.isAdmin || a.role.includes("products"),
  },
  {
    to: "/inventoryLogs",
    label: "Inventory logs",
    icon: "bi-blockquote-left",
    show: (a) => a.isAdmin || a.role === "inventoryLogs",
  },
  {
    to: "/logs",
    label: "Logs",
    icon: "bi-book",
    show: (a) => a.isAdmin || a.role.includes("inventorytracking"),
  },
  {
    to: "/webhooks",
    label: "Webhooks",
    icon: "bi-browser-edge",
    show: (a) => a.isAdmin || a.role === "webhooks",
  },
  { to: "/stores", label: "Stores", icon: "bi-shop", show: (a) => a.isAdmin },
  {
    to: "/couriers",
    label: "Couriers",
    icon: "bi-truck",
    show: (a) => a.isAdmin,
  },
  {
    to: "/loadsheet",
    label: "Load sheet",
    icon: "bi-bus-front",
    show: (a) => a.isAdmin || a.role === "loadSheet",
  },
  {
    to: "/orders/paid",
    label: "Orders paid by month",
    icon: "bi-credit-card-2-back",
    show: (a) => a.isAdmin || a.role === "orders",
  },
  {
    to: "/laam",
    label: "Laam",
    icon: "bi-shop-window",
    show: (a) => a.isAdmin || a.role === "laam_orders",
  },
];

const roleLabel = (a) =>
  a.u.users_role || a.u.role || (a.isAdmin ? "admin" : "member");

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "U";

/* ---------- styles (scoped with vn- prefix, Bootstrap stays) ---------- */
const css = `
.vn-shell{display:flex;height:100vh;background:#fbfcfb}
.vn-side{width:236px;flex:none;display:flex;flex-direction:column;background:#f2f4f3;border-right:1px solid #dde3e0;transition:width .15s ease}
.vn-side.is-collapsed{width:68px}
.vn-brand{display:flex;align-items:center;justify-content:space-between;padding:16px 14px 12px 16px;min-height:60px}
.vn-brand b{font-size:18px;letter-spacing:-.01em;color:#0b5d44}
.vn-toggle{border:0;background:transparent;color:#4a5a54;border-radius:8px;padding:6px 8px;line-height:1}
.vn-toggle:hover{background:#e3e9e6}
.vn-nav{flex:1;overflow-y:auto;padding:4px 10px;display:flex;flex-direction:column;gap:2px;list-style:none;margin:0}
.vn-link{display:flex;align-items:center;gap:12px;padding:9px 12px;border-radius:8px;color:#31403b;text-decoration:none;font-size:14px;font-weight:500;white-space:nowrap}
.vn-link i,.vn-link svg{font-size:18px;width:20px;flex:none;text-align:center}
.vn-link:hover{background:#e6ebe8;color:#1d2a26}
.vn-link.active{background:#fff;color:#0b5d44;font-weight:600;box-shadow:inset 3px 0 0 #0b5d44,0 0 0 1px #dde3e0}
.vn-link:focus-visible,.vn-toggle:focus-visible,.vn-mini:focus-visible{outline:2px solid #0b5d44;outline-offset:2px}
.is-collapsed .vn-label,.is-collapsed .vn-who{display:none}
.is-collapsed .vn-link{justify-content:center;padding:9px 0}
.vn-user{border-top:1px solid #dde3e0;padding:12px}
.vn-me{display:flex;align-items:center;gap:10px;margin-bottom:8px}
.vn-avatar{width:34px;height:34px;flex:none;border-radius:50%;background:#0b5d44;color:#fff;display:grid;place-items:center;font-size:13px;font-weight:600}
.vn-who{min-width:0;line-height:1.2}
.vn-who strong{display:block;font-size:13px;color:#1d2a26;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vn-who span{font-size:12px;color:#5b6b65;text-transform:capitalize}
.vn-actions{display:flex;gap:6px}
.is-collapsed .vn-actions{flex-direction:column}
.vn-mini{flex:1;border:1px solid #cfd8d4;background:#fff;color:#31403b;border-radius:8px;padding:6px 8px;font-size:13px;display:flex;align-items:center;justify-content:center;gap:6px}
.vn-mini:hover{background:#e6ebe8}
.vn-main{flex:1;min-width:0;display:flex;flex-direction:column}
.vn-top{background:#fff;border-bottom:1px solid #dde3e0;padding:10px 20px}
.vn-content{flex:1;overflow-y:auto;padding:16px 20px}
.vn-empty{max-width:420px;margin:72px auto;text-align:left}
.vn-empty h5{font-weight:600;margin-bottom:6px}
@media (max-width:767px){.vn-top{padding:8px 12px}.vn-content{padding:12px}}
@media (prefers-reduced-motion:reduce){.vn-side{transition:none}}
@media print{.vn-side,.vn-top{display:none}.vn-shell{display:block;height:auto}}
`;

const Dashboard = () => {
  const { isStoreSelected } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, dispatch } = useContext(AuthContext);
  const [unread, setUnread] = useState(0);
  const [storeCount, setStoreCount] = useState(null); // null = not loaded yet

  const [collapsed, setCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768,
  );

  const access = useMemo(() => getAccess(user), [user]);

  useEffect(() => {
    if (!user) navigate("/login", { replace: true });
    if (!user.store_id) navigate("/shopify/connect", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (location.pathname === "/chats") setUnread(0);
  }, [location.pathname]);

  useEffect(() => {
    if (storeCount === 0 && access.isAdmin) {
      navigate("/shopify/connect", { replace: true });
    }
  }, [storeCount, access.isAdmin, navigate]);
  const items = useMemo(
    () => [
      {
        to: "/chats",
        label: "Chats",
        icon: (
          <Badge badgeContent={unread} color="error" max={99}>
            <WhatsAppIcon fontSize="small" />
          </Badge>
        ),
      },
      ...NAV_ITEMS.filter((i) => i.show(access)).map((i) => ({
        to: i.to,
        label: i.label,
        icon: <i className={`bi ${i.icon}`} aria-hidden="true" />,
      })),
    ],
    [access, unread],
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="vn-shell">
      <style>{css}</style>

      <aside className={`vn-side no-print ${collapsed ? "is-collapsed" : ""}`}>
        <div className="vn-brand">
          {!collapsed && <b>vnoirs</b>}
          <button
            type="button"
            className="vn-toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          >
            <i
              className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-left"}`}
            />
          </button>
        </div>

        <ul className="vn-nav">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end
                title={item.label}
                className={({ isActive }) =>
                  "vn-link" + (isActive ? " active" : "")
                }
              >
                {item.icon}
                <span className="vn-label">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="vn-user">
          <div className="vn-me">
            <div className="vn-avatar" aria-hidden="true">
              {initials(access.u.full_name || access.u.username)}
            </div>
            <div className="vn-who">
              <strong>
                {access.u.full_name || access.u.username || "Account"}
              </strong>
              <span>{roleLabel(access)}</span>
            </div>
          </div>
          <div className="vn-actions">
            <button
              type="button"
              className="vn-mini"
              onClick={() => navigate("/users")}
              title="Profile"
            >
              <i className="bi bi-person" />
              {!collapsed && "Profile"}
            </button>
            <button
              type="button"
              className="vn-mini"
              onClick={handleLogout}
              title="Log out"
            >
              <i className="bi bi-box-arrow-right" />
              {!collapsed && "Log out"}
            </button>
          </div>
        </div>
      </aside>

      <div className="vn-main">
        <div className="vn-top no-print">
          <StoreSelector setUnread={setUnread} onStoresLoaded={setStoreCount} />
        </div>

        <main className="vn-content text-center">
          {!isStoreSelected ? (
            <div className="vn-empty">
              <h5>Select a store to continue</h5>
              <p className="text-muted mb-3">
                Pick a store from the list above to see its orders and chats.
              </p>
              {access.isAdmin && (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => navigate("/shopify/connect")}
                >
                  Connect a Shopify store
                </button>
              )}
            </div>
          ) : (
            <>
              {access.isAdmin && location.pathname === "/" && <MyChart />}
              <Outlet />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
